import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { iRootState, iRootDispatch } from '../../store';
import {
  getAllDashboards,
  FilterParams,
  getPerformanceByEstate,
  getTableData,
  TableDataResponse,
  getBondInsuranceData,
  BondInsuranceResponse,
  getCapitalizationData,
  CapitalizationResponse,
  getFireInsuranceData,
  FireInsuranceResponse,
  getClaimsData,
  ClaimsResponse,
  downloadDashboardReport
} from '../../services/dashboard';
import { UsersToCustomDropDownOptions } from '../../util';
import { DateToStringDate, subtractMonthToDate } from '../../services/date';
import { Container } from './styles';
import { useDropDownUsers } from '../../services/broker';

import FilterBar from './components/FilterBar';
import ChartsSection from './components/ChartsSection';
import DashboardTable from './components/DashboardTable';
import BondInsuranceTable from './components/BondInsuranceTable';
import CapitalizationTable from './components/CapitalizationTable';
import FireInsuranceTable from './components/FireInsuranceTable';
import ClaimsTable from './components/ClaimsTable';
import { dangerNotification, successNotification } from '../../services/notification';

enum FilterEnum {
  bondInsurancesTotal = 'biTotal',
  bondInsurancesApproveds = 'biApproveds',
  bondInsurancePolicyCreated = 'biPolicyCreated',
  bondInsuranceHired = 'biHired',
  bondInsuranceWaiting = 'biWaiting',
  bondInsuranceToExpire = 'biToExpire',
  bondInsurancesCancelled = 'biCancelled',
  bondInsuranceRenewed = 'biRenewed',
  policyToExpire = 'policyToExpire',
  fireInsurancesTotal = 'fiTotal',
  fireInsurancesPendentPayment = 'fiPendentPayment',
  fireInsurancesToExpire = 'fiToExpire',
  fireInsurancesPolicyToExpire = 'fiPolicyToExpire',
  fireInsurancesPolicyWaiting = 'fiPolicyWaiting',
  fireInsurancesPolicyHired = 'fiPolicyHired',
  fireInsurancesCancelled = 'fiCancelled',
  capitalizationsInProgress = 'capitalizationsInProgress',
  capitalizationsWaitingPayment = 'capitalizationsWaitingPayment',
  capitalizationsHired = 'capitalizationsHired',
  capitalizationsCancelled = 'capitalizationsCancelled'
}

const mapState = (state: iRootState) => {
  return {
    user: state.user,
    availableBrokersAndEstates: state.availableBrokersAndEstates
  };
};

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateCurrentPage: (state: any) => dispatch.currentPage.updateCurrentPage(state)
});

const FranchisesDashboard = (props: any) => {
  const { user } = props;

  const [activeMenu, setActiveMenu] = useState<string>('dashboard');

  const getInitialDates = () => {
    const today = new Date();
    const thirtyDaysAgo = subtractMonthToDate(today, 1);

    return {
      startDate: DateToStringDate(thirtyDaysAgo),
      endDate: DateToStringDate(today)
    };
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [franchisesLoading, setFranchisesLoading] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const initialDates = getInitialDates();

  const [franchise, setFranchise] = useState('Todos');
  const [estate, setEstate] = useState('Todos');
  const [manager, setManager] = useState('Todos');
  const [startDateInput, setStartDateInput] = useState(initialDates.startDate);
  const [endDateInput, setEndDateInput] = useState(initialDates.endDate);

  const [franchises, setFranchises] = useState<any[]>([]);
  const [estates, setEstates] = useState<any[]>([]);

  const { data: managersData, isLoading: managersLoading } = useDropDownUsers(
    user?.broker?.id
  );

  const franchiseOptions = useMemo(() => {
    return [{ key: -1, text: 'Todos', value: 'Todos' }, ...franchises];
  }, [franchises]);

  const estateOptions = useMemo(() => {
    return [{ key: -1, text: 'Todos', value: 'Todos' }, ...estates];
  }, [estates]);

  const managerOptions = useMemo(() => {
    if (!managersData) return [{ key: -1, text: 'Todos', value: 'Todos' }];

    const formattedManagers = managersData.map((user: any) => ({
      id: user.id,
      name: user.name
    }));

    const options = UsersToCustomDropDownOptions(false, formattedManagers);
    return [{ key: -1, text: 'Todos', value: 'Todos' }, ...options];
  }, [managersData]);

  const [biApproveds, setBiApproveds] = useState<number>(0);
  const [biHired, setBiHired] = useState<number>(0);
  const [biRenewed, setBiRenewed] = useState<number>(0);
  const [biCancelled, setBiCancelled] = useState<number>(0);

  //Dados do dashboard - Capitalização
  const [capitalizationsInProgress, setCapitalizationsInProgress] = useState<number>(0);
  const [capitalizationsHired, setCapitalizationsHired] = useState<number>(0);
  const [capitalizationsWaitingPayment, setCapitalizationsWaitingPayment] =
    useState<number>(0);
  const [capitalizationsCancelled, setCapitalizationsCancelled] = useState<number>(0);

  //Dados do dashboard - Seguro Incêndio
  const [fiTotal, setFiTotal] = useState<number>(0);
  const [fiPolicyHired, setFiPolicyHired] = useState<number>(0);
  const [fiPolicyWaiting, setFiPolicyWaiting] = useState<number>(0);
  const [fiCancelled, setFiCancelled] = useState<number>(0);

  //Dados de performance
  const [bondInsurancePerformance, setBondInsurancePerformance] = useState<any[]>([]);
  const [capitalizationPerformance, setCapitalizationPerformance] = useState<any[]>([]);
  const [fireInsurancePerformance, setFireInsurancePerformance] = useState<any[]>([]);

  const colors = {
    canceled: 'rgb(255, 99, 132)',
    waiting: 'rgb(255, 205, 86)',
    pending: 'rgb(54, 162, 235)',
    approved: 'rgb(75, 192, 192)',
    quotations: 'rgb(255, 205, 86)',
    issued: 'rgb(75, 192, 192)',
    redemptionsRequested: 'rgb(54, 162, 235)',
    redemptionsCompleted: 'rgb(75, 192, 192)'
  };

  const [realTableData, setRealTableData] = useState<TableDataResponse>({
    data: [],
    total: 0
  });

  const [bondInsuranceTableData, setBondInsuranceTableData] =
    useState<BondInsuranceResponse>({
      data: [],
      total: 0
    });

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [bondInsuranceCurrentPage, setBondInsuranceCurrentPage] = useState<number>(1);
  const [bondInsuranceTotalItems, setBondInsuranceTotalItems] = useState<number>(0);

  const [capitalizationTableData, setCapitalizationTableData] =
    useState<CapitalizationResponse>({
      data: [],
      total: 0
    });
  const [capitalizationCurrentPage, setCapitalizationCurrentPage] = useState<number>(1);
  const [capitalizationTotalItems, setCapitalizationTotalItems] = useState<number>(0);

  const [fireInsuranceTableData, setFireInsuranceTableData] =
    useState<FireInsuranceResponse>({
      data: [],
      total: 0
    });
  const [fireInsuranceCurrentPage, setFireInsuranceCurrentPage] = useState<number>(1);
  const [fireInsuranceTotalItems, setFireInsuranceTotalItems] = useState<number>(0);

  const [claimsTableData, setClaimsTableData] = useState<ClaimsResponse>({
    data: [],
    total: 0
  });
  const [claimsCurrentPage, setClaimsCurrentPage] = useState<number>(1);
  const [claimsTotalItems, setClaimsTotalItems] = useState<number>(0);

  const itemsPerPage = 10;

  const tableData = realTableData.data || [];

  const clearAllDataStates = () => {
    setBiApproveds(0);
    setBiHired(0);
    setBiRenewed(0);
    setBiCancelled(0);
    setBondInsurancePerformance([]);
    setBondInsuranceTableData({ data: [], total: 0 });
    setBondInsuranceTotalItems(0);

    setCapitalizationsInProgress(0);
    setCapitalizationsHired(0);
    setCapitalizationsWaitingPayment(0);
    setCapitalizationsCancelled(0);
    setCapitalizationPerformance([]);
    setCapitalizationTableData({ data: [], total: 0 });
    setCapitalizationTotalItems(0);

    setFiTotal(0);
    setFiPolicyHired(0);
    setFiPolicyWaiting(0);
    setFiCancelled(0);
    setFireInsurancePerformance([]);
    setFireInsuranceTableData({ data: [], total: 0 });
    setFireInsuranceTotalItems(0);

    setRealTableData({ data: [], total: 0 });
    setTotalItems(0);

    setClaimsTableData({ data: [], total: 0 });
    setClaimsTotalItems(0);
  };

  const fetchTableData = async () => {
    if (franchises.length === 0) {
      return;
    }

    if (franchises.length > 1 && franchise === 'Todos') {
      return;
    }

    try {
      setTableLoading(true);

      const response = await getTableData(
        {
          franchiseId: franchise !== 'Todos' ? franchise : undefined,
          estateId: estate !== 'Todos' ? estate : undefined,
          managerId: manager !== 'Todos' ? manager : undefined,
          startDate: formatDateToAPI(startDateInput),
          endDate: formatDateToAPI(endDateInput),
          page: currentPage,
          limit: itemsPerPage
        },
        itemsPerPage
      );

      setRealTableData(response);
      setTotalItems(response.total);
    } catch (error) {
      console.error('Erro ao buscar dados da tabela:', error);
      setRealTableData({ data: [], total: 0 });
      setTotalItems(0);
    } finally {
      setTableLoading(false);
    }
  };

  const fetchBondInsuranceData = async () => {
    if (franchises.length === 0) {
      return;
    }

    if (franchises.length > 1 && franchise === 'Todos') {
      return;
    }

    try {
      setTableLoading(true);

      const response = await getBondInsuranceData({
        franchiseId: franchise !== 'Todos' ? franchise : undefined,
        estateId: estate !== 'Todos' ? estate : undefined,
        managerId: manager !== 'Todos' ? manager : undefined,
        startDate: formatDateToAPI(startDateInput),
        endDate: formatDateToAPI(endDateInput),
        page: bondInsuranceCurrentPage,
        limit: itemsPerPage
      });

      setBondInsuranceTableData(response);
      setBondInsuranceTotalItems(response.total);
    } catch (error) {
      console.error('Erro ao buscar dados da tabela de Seguro Fiança:', error);
      setBondInsuranceTableData({ data: [], total: 0 });
      setBondInsuranceTotalItems(0);
    } finally {
      setTableLoading(false);
    }
  };

  const fetchCapitalizationData = async () => {
    if (franchises.length === 0) {
      return;
    }

    if (franchises.length > 1 && franchise === 'Todos') {
      return;
    }

    try {
      setTableLoading(true);

      const response = await getCapitalizationData({
        franchiseId: franchise !== 'Todos' ? franchise : undefined,
        estateId: estate !== 'Todos' ? estate : undefined,
        managerId: manager !== 'Todos' ? manager : undefined,
        startDate: formatDateToAPI(startDateInput),
        endDate: formatDateToAPI(endDateInput),
        page: capitalizationCurrentPage,
        limit: itemsPerPage
      });

      setCapitalizationTableData(response);
      setCapitalizationTotalItems(response.total);
    } catch (error) {
      console.error('Erro ao buscar dados de Capitalização:', error);
      setCapitalizationTableData({ data: [], total: 0 });
      setCapitalizationTotalItems(0);
    } finally {
      setTableLoading(false);
    }
  };

  const fetchFireInsuranceData = async () => {
    if (franchises.length === 0) {
      return;
    }

    if (franchises.length > 1 && franchise === 'Todos') {
      return;
    }

    try {
      setTableLoading(true);

      const response = await getFireInsuranceData({
        franchiseId: franchise !== 'Todos' ? franchise : undefined,
        estateId: estate !== 'Todos' ? estate : undefined,
        managerId: manager !== 'Todos' ? manager : undefined,
        startDate: formatDateToAPI(startDateInput),
        endDate: formatDateToAPI(endDateInput),
        page: fireInsuranceCurrentPage,
        limit: itemsPerPage
      });

      setFireInsuranceTableData(response);
      setFireInsuranceTotalItems(response.total);
    } catch (error) {
      console.error('Erro ao buscar dados de Seguro Incêndio:', error);
      setFireInsuranceTableData({ data: [], total: 0 });
      setFireInsuranceTotalItems(0);
    } finally {
      setTableLoading(false);
    }
  };

  const fetchClaimsData = async () => {
    if (franchises.length === 0) {
      return;
    }

    if (franchises.length > 1 && franchise === 'Todos') {
      return;
    }

    try {
      setTableLoading(true);

      const response = await getClaimsData({
        franchiseId: franchise !== 'Todos' ? franchise : undefined,
        estateId: estate !== 'Todos' ? estate : undefined,
        managerId: manager !== 'Todos' ? manager : undefined,
        startDate: formatDateToAPI(startDateInput),
        endDate: formatDateToAPI(endDateInput),
        page: claimsCurrentPage,
        limit: itemsPerPage
      });

      setClaimsTableData(response);
      setClaimsTotalItems(response.total);
    } catch (error) {
      console.error('Erro ao buscar dados de Sinistros:', error);
      setClaimsTableData({ data: [], total: 0 });
      setClaimsTotalItems(0);
    } finally {
      setTableLoading(false);
    }
  };

  const fetchData = async (filter: string) => {
    try {
      if (franchises.length === 0) {
        return;
      }

      if (franchises.length > 1 && franchise === 'Todos') {
        return;
      }

      const filterParams: FilterParams = {};

      if (franchise !== 'Todos') {
        filterParams.franchiseId = franchise;
      }

      if (estate !== 'Todos') {
        filterParams.estateId = estate;
      }

      if (manager !== 'Todos') {
        filterParams.managerId = manager;
      }

      if (startDateInput) {
        filterParams.startDate = formatDateToAPI(startDateInput);
      }

      if (endDateInput) {
        filterParams.endDate = formatDateToAPI(endDateInput);
      }

      const response = await getAllDashboards(filter, filterParams);

      switch (filter) {
        case FilterEnum.bondInsurancesApproveds:
          setBiApproveds(response);
          break;
        case FilterEnum.bondInsuranceHired:
          setBiHired(response);
          break;
        case FilterEnum.bondInsuranceRenewed:
          setBiRenewed(response);
          break;
        case FilterEnum.bondInsurancesCancelled:
          setBiCancelled(response);
          break;
        case FilterEnum.capitalizationsHired:
          setCapitalizationsHired(response);
          break;
        case FilterEnum.capitalizationsInProgress:
          setCapitalizationsInProgress(response);
          break;
        case FilterEnum.capitalizationsWaitingPayment:
          setCapitalizationsWaitingPayment(response);
          break;
        case FilterEnum.capitalizationsCancelled:
          setCapitalizationsCancelled(response);
          break;
        case FilterEnum.fireInsurancesTotal:
          setFiTotal(response);
          break;
        case FilterEnum.fireInsurancesPolicyHired:
          setFiPolicyHired(response);
          break;
        case FilterEnum.fireInsurancesPolicyWaiting:
          setFiPolicyWaiting(response);
          break;
        case FilterEnum.fireInsurancesCancelled:
          setFiCancelled(response);
          break;
        default:
          break;
      }
    } catch (err) {
      console.log('ERRO', err);
    }
  };

  const formatDateToAPI = (dateString: string) => {
    if (!dateString) return undefined;

    const parts = dateString.split('/');
    if (parts.length !== 3) return undefined;

    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  const handleSearch = () => {
    if (franchises.length === 0) {
      dangerNotification(
        'Atenção',
        'Nenhuma franquia encontrada. Não é possível realizar a busca.'
      );
      return;
    }

    if (franchises.length > 1 && franchise === 'Todos') {
      dangerNotification(
        'Atenção',
        'Por favor, selecione uma franquia para realizar a busca.'
      );
      return;
    }

    if (activeMenu === 'dashboard') {
      fetchAllDashboardData();
    } else if (activeMenu === 'seguroFianca') {
      fetchBondInsuranceData();
    } else if (activeMenu === 'capitalizacao') {
      fetchCapitalizationData();
    } else if (activeMenu === 'seguroIncendio') {
      fetchFireInsuranceData();
    } else if (activeMenu === 'sinistros') {
      fetchClaimsData();
    }
  };

  const fetchAllDashboardData = async () => {
    if (franchises.length === 0) {
      return;
    }

    if (franchises.length > 1 && franchise === 'Todos') {
      return;
    }

    try {
      setIsLoading(true);

      await fetchData(FilterEnum.bondInsurancesApproveds);
      await fetchData(FilterEnum.bondInsuranceHired);
      await fetchData(FilterEnum.bondInsuranceRenewed);
      await fetchData(FilterEnum.bondInsurancesCancelled);

      await fetchData(FilterEnum.capitalizationsInProgress);
      await fetchData(FilterEnum.capitalizationsHired);
      await fetchData(FilterEnum.capitalizationsWaitingPayment);
      await fetchData(FilterEnum.capitalizationsCancelled);

      await fetchData(FilterEnum.fireInsurancesTotal);
      await fetchData(FilterEnum.fireInsurancesPolicyHired);
      await fetchData(FilterEnum.fireInsurancesPolicyWaiting);
      await fetchData(FilterEnum.fireInsurancesCancelled);

      try {
        const commonParams = {
          franchiseId: franchise !== 'Todos' ? franchise : undefined,
          estateId: estate !== 'Todos' ? estate : undefined,
          ...(manager !== 'Todos' && { managerId: manager?.toString() }),
          startDate: formatDateToAPI(startDateInput),
          endDate: formatDateToAPI(endDateInput)
        };

        const fiancaPerformanceData = await getPerformanceByEstate(
          'fianca',
          commonParams,
          7
        ); // Limitando a 7 imobiliárias
        setBondInsurancePerformance(fiancaPerformanceData || []);

        const capitalizacaoPerformanceData = await getPerformanceByEstate(
          'capitalizacao',
          commonParams,
          7
        );
        setCapitalizationPerformance(capitalizacaoPerformanceData || []);

        const incendioPerformanceData = await getPerformanceByEstate(
          'incendio',
          commonParams,
          7
        );
        setFireInsurancePerformance(incendioPerformanceData || []);
      } catch (error) {
        console.error('Erro ao buscar dados de desempenho:', error);
      }

      await fetchTableData();
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearFilters = () => {
    if (franchises.length === 0) {
      setFranchise('Todos');
      clearAllDataStates();
      dangerNotification(
        'Atenção',
        'Nenhuma franquia encontrada. Não é possível realizar a busca.'
      );
      return;
    } else if (franchises.length === 1) {
      setFranchise(franchises[0].value);
    } else {
      setFranchise('Todos');
      clearAllDataStates();
    }

    setEstate('Todos');
    setManager('Todos');

    const refreshedDates = getInitialDates();
    setStartDateInput(refreshedDates.startDate);
    setEndDateInput(refreshedDates.endDate);

    setTimeout(() => {
      if (franchises.length === 1) {
        if (activeMenu === 'dashboard') {
          fetchAllDashboardData();
        } else if (activeMenu === 'seguroFianca') {
          fetchBondInsuranceData();
        } else if (activeMenu === 'capitalizacao') {
          fetchCapitalizationData();
        } else if (activeMenu === 'seguroIncendio') {
          fetchFireInsuranceData();
        } else if (activeMenu === 'sinistros') {
          fetchClaimsData();
        }
      }
    }, 100);
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);

      await downloadDashboardReport({
        franchiseId: franchise !== 'Todos' ? franchise : undefined,
        estateId: estate !== 'Todos' ? estate : undefined,
        managerId: manager !== 'Todos' ? manager : undefined,
        startDate: formatDateToAPI(startDateInput),
        endDate: formatDateToAPI(endDateInput)
      });

      successNotification(
        'Solicitação de relatório',
        'O relatório foi solicitado com sucesso. Você receberá um e-mail com o link para download.'
      );
    } catch (error) {
      console.error('Erro ao baixar o relatório:', error);
      dangerNotification(
        'Erro ao baixar o relatório',
        'Ocorreu um erro ao solicitar o relatório. Por favor, tente novamente.'
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: false
    },
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            fontSize: 10,
            padding: 5,
            precision: 0,
            callback: function (value: number) {
              if (value % 1 === 0) {
                return value;
              }
              return null;
            },
            maxTicksLimit: 6
          },
          gridLines: {
            drawBorder: false
          }
        }
      ],
      xAxes: [
        {
          ticks: {
            fontSize: 10,
            padding: 5
          },
          gridLines: {
            display: false
          }
        }
      ]
    }
  };

  const optionsHorizontal = {
    responsive: true,
    maintainAspectRatio: true,
    legend: {
      display: false
    },
    indexAxis: 'y',
    scales: {
      xAxes: [
        {
          ticks: {
            beginAtZero: true,
            fontSize: 10,
            padding: 5,
            precision: 0, // Evita decimais
            callback: function (value: number) {
              if (value % 1 === 0) {
                // Se for número inteiro
                return value;
              }
              return null; // Não mostra ticks com valores decimais
            },
            maxTicksLimit: 8 // Limita o número de ticks no eixo
          },
          gridLines: {
            drawBorder: false
          }
        }
      ],
      yAxes: [
        {
          ticks: {
            fontSize: 10,
            padding: 5,
            precision: 0,
            callback: function (value: number) {
              if (value % 1 === 0) {
                return value;
              }
              return null;
            }
          },
          gridLines: {
            display: false
          }
        }
      ]
    },
    plugins: {
      legend: {
        display: false
      }
    }
  };

  const bondInsuranceData = {
    labels: ['Cancelados', 'Aprovados', 'Contratados', 'Renovados'],
    datasets: [
      {
        data: [
          biCancelled || 0, // Canceled
          biApproveds || 0, // Approved
          biHired || 0, // Contratados
          biRenewed || 0 // Renovados
        ],
        backgroundColor: [
          colors.canceled,
          colors.approved,
          colors.pending,
          colors.quotations
        ],
        borderColor: [
          colors.canceled,
          colors.approved,
          colors.pending,
          colors.quotations
        ],
        borderWidth: 1
      }
    ]
  };

  const capitalizationContractsData = {
    labels: ['Cancelados', 'Emitidos', 'Resgates solicitados', 'Resgate concluído'],
    datasets: [
      {
        data: [
          capitalizationsCancelled || 0, // Canceled
          capitalizationsHired || 0, // Issued
          capitalizationsWaitingPayment || 0, // Redemptions requested
          capitalizationsInProgress || 0 // Redemption completed
        ],
        backgroundColor: [
          colors.canceled,
          colors.issued,
          colors.redemptionsRequested,
          colors.redemptionsCompleted
        ],
        borderColor: [
          colors.canceled,
          colors.issued,
          colors.redemptionsRequested,
          colors.redemptionsCompleted
        ],
        borderWidth: 1
      }
    ]
  };

  const fireInsuranceData = {
    labels: ['Cancelados', 'Orçamentos', 'Em espera', 'Emitidos'],
    datasets: [
      {
        data: [
          fiCancelled || 0, // Canceled
          fiTotal || 0, // Quotations
          fiPolicyWaiting || 0, // Waiting
          fiPolicyHired || 0 // Issued
        ],
        backgroundColor: [
          colors.canceled,
          colors.quotations,
          colors.waiting,
          colors.issued
        ],
        borderColor: [colors.canceled, colors.quotations, colors.waiting, colors.issued],
        borderWidth: 1
      }
    ]
  };

  const bondInsurancePerformanceData = {
    labels: Array.isArray(bondInsurancePerformance)
      ? bondInsurancePerformance.map(item => item.estateName)
      : [],
    datasets: [
      {
        data: Array.isArray(bondInsurancePerformance)
          ? bondInsurancePerformance.map(item => item.value)
          : [],
        backgroundColor: 'rgb(54, 162, 235)',
        borderColor: 'rgb(54, 162, 235)',
        borderWidth: 1
      }
    ]
  };

  const capitalizationPerformanceData = {
    labels: Array.isArray(capitalizationPerformance)
      ? capitalizationPerformance.map(item => item.estateName)
      : [],
    datasets: [
      {
        data: Array.isArray(capitalizationPerformance)
          ? capitalizationPerformance.map(item => item.value)
          : [],
        backgroundColor: 'rgb(54, 162, 235)',
        borderColor: 'rgb(54, 162, 235)',
        borderWidth: 1
      }
    ]
  };

  const fireInsurancePerformanceData = {
    labels: Array.isArray(fireInsurancePerformance)
      ? fireInsurancePerformance.map(item => item.estateName)
      : [],
    datasets: [
      {
        data: Array.isArray(fireInsurancePerformance)
          ? fireInsurancePerformance.map(item => item.value)
          : [],
        backgroundColor: 'rgb(54, 162, 235)',
        borderColor: 'rgb(54, 162, 235)',
        borderWidth: 1
      }
    ]
  };

  // Efeito para carregar os dados das franquias e imobiliárias do Redux
  useEffect(() => {
    // Lida com props indefinidos primeiro
    if (!props.availableBrokersAndEstates) {
      setFranchises([]);
      setEstates([]);
      setFranchise('Todos');
      clearAllDataStates();
      // Opcionalmente, uma notificação se isso não for um estado esperado
      return;
    }

    // Configura estates
    if (props.availableBrokersAndEstates.estates) {
      setEstates(props.availableBrokersAndEstates.estates);
    } else {
      setEstates([]); // Garante que estates seja um array
    }

    // Configura franchises
    if (props.availableBrokersAndEstates.franchises) {
      const availableFranchises = props.availableBrokersAndEstates.franchises;
      setFranchises(availableFranchises);

      if (availableFranchises.length === 0) {
        setFranchise('Todos');
        clearAllDataStates();
        dangerNotification('Atenção', 'Nenhuma franquia encontrada.');
      } else if (availableFranchises.length === 1) {
        setFranchise(availableFranchises[0].value);
        // A busca será acionada pelo useEffect que observa 'franchise' e 'activeMenu'
      } else {
        // Múltiplas franquias
        setFranchise('Todos');
        clearAllDataStates();
        // Nenhuma busca automática, aguarda seleção
      }
    } else {
      // Sem props.availableBrokersAndEstates.franchises
      setFranchises([]);
      setFranchise('Todos');
      clearAllDataStates();
      dangerNotification('Atenção', 'Lista de franquias não disponível.');
    }
    // NENHUMA chamada fetchAllDashboardData ou similar aqui diretamente.
  }, [props.availableBrokersAndEstates]);

  // Adicionar um useEffect específico para atualizar a tabela quando currentPage mudar
  useEffect(() => {
    if (
      !!estates.length &&
      activeMenu === 'dashboard' &&
      (franchises?.length === 1 || (franchises?.length > 1 && franchise !== 'Todos'))
    ) {
      fetchTableData();
    }
  }, [currentPage, activeMenu, franchise, franchises.length]);

  useEffect(() => {
    if (
      !!estates.length &&
      activeMenu === 'seguroFianca' &&
      (franchises?.length === 1 || (franchises?.length > 1 && franchise !== 'Todos'))
    ) {
      fetchBondInsuranceData();
    }
  }, [bondInsuranceCurrentPage, activeMenu, franchise, franchises.length]);

  useEffect(() => {
    if (
      !!estates.length &&
      activeMenu === 'capitalizacao' &&
      (franchises?.length === 1 || (franchises?.length > 1 && franchise !== 'Todos'))
    ) {
      fetchCapitalizationData();
    }
  }, [capitalizationCurrentPage, activeMenu, franchise, franchises.length]);

  useEffect(() => {
    if (
      !!estates.length &&
      activeMenu === 'seguroIncendio' &&
      (franchises?.length === 1 || (franchises?.length > 1 && franchise !== 'Todos'))
    ) {
      fetchFireInsuranceData();
    }
  }, [fireInsuranceCurrentPage, activeMenu, franchise, franchises.length]);

  useEffect(() => {
    if (
      !!estates.length &&
      activeMenu === 'sinistros' &&
      (franchises?.length === 1 || (franchises?.length > 1 && franchise !== 'Todos'))
    ) {
      fetchClaimsData();
    }
  }, [claimsCurrentPage, activeMenu, franchise, franchises.length]);

  useEffect(() => {
    if (!!estates.length) {
      setCurrentPage(1);
      setBondInsuranceCurrentPage(1);
      setCapitalizationCurrentPage(1);
      setFireInsuranceCurrentPage(1);
      setClaimsCurrentPage(1);
    }
  }, [franchise, estate, manager, startDateInput, endDateInput]);

  // Adicionar um useEffect para atualizar os dados quando o menu ativo mudar
  useEffect(() => {
    if (franchises?.length === 1 || (franchises?.length > 1 && franchise !== 'Todos')) {
      if (activeMenu === 'dashboard') {
        fetchAllDashboardData();
      } else if (activeMenu === 'seguroFianca') {
        fetchBondInsuranceData();
      } else if (activeMenu === 'capitalizacao') {
        fetchCapitalizationData();
      } else if (activeMenu === 'seguroIncendio') {
        fetchFireInsuranceData();
      } else if (activeMenu === 'sinistros') {
        fetchClaimsData();
      }
    }
  }, [activeMenu, franchise, franchises?.length]);

  return (
    <Container>
      <FilterBar
        franchise={franchise}
        setFranchise={setFranchise}
        estate={estate}
        setEstate={setEstate}
        manager={manager}
        setManager={setManager}
        startDateInput={startDateInput}
        setStartDateInput={setStartDateInput}
        endDateInput={endDateInput}
        setEndDateInput={setEndDateInput}
        franchiseOptions={franchiseOptions}
        estateOptions={estateOptions}
        managerOptions={managerOptions}
        handleSearch={handleSearch}
        handleClearFilters={handleClearFilters}
        handleDownload={handleDownload}
        isLoading={isLoading}
        isDownloading={isDownloading}
        franchisesLoading={franchisesLoading}
        managersLoading={managersLoading}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        userRole={props.user.role}
      />

      {activeMenu === 'dashboard' && (
        <>
          <ChartsSection
            isLoading={isLoading}
            bondInsuranceData={bondInsuranceData}
            capitalizationContractsData={capitalizationContractsData}
            fireInsuranceData={fireInsuranceData}
            bondInsurancePerformanceData={bondInsurancePerformanceData}
            capitalizationPerformanceData={capitalizationPerformanceData}
            fireInsurancePerformanceData={fireInsurancePerformanceData}
            options={options}
            optionsHorizontal={optionsHorizontal}
          />

          <DashboardTable
            tableData={tableData}
            tableLoading={tableLoading}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalItems={totalItems}
          />
        </>
      )}

      {activeMenu === 'seguroFianca' && (
        <BondInsuranceTable
          tableData={bondInsuranceTableData.data}
          tableLoading={tableLoading}
          currentPage={bondInsuranceCurrentPage}
          setCurrentPage={setBondInsuranceCurrentPage}
          totalItems={bondInsuranceTotalItems}
        />
      )}

      {activeMenu === 'capitalizacao' && (
        <CapitalizationTable
          tableData={capitalizationTableData.data}
          tableLoading={tableLoading}
          currentPage={capitalizationCurrentPage}
          setCurrentPage={setCapitalizationCurrentPage}
          totalItems={capitalizationTotalItems}
        />
      )}

      {activeMenu === 'seguroIncendio' && (
        <FireInsuranceTable
          tableData={fireInsuranceTableData.data}
          tableLoading={tableLoading}
          currentPage={fireInsuranceCurrentPage}
          setCurrentPage={setFireInsuranceCurrentPage}
          totalItems={fireInsuranceTotalItems}
        />
      )}

      {activeMenu === 'sinistros' && (
        <ClaimsTable
          tableData={claimsTableData.data}
          tableLoading={tableLoading}
          currentPage={claimsCurrentPage}
          setCurrentPage={setClaimsCurrentPage}
          totalItems={claimsTotalItems}
        />
      )}
    </Container>
  );
};

export default connect(mapState, mapDispatch)(FranchisesDashboard);
